import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import JobSearchFilters from '../components/Jobs/JobSearchFilters';
import JobList from '../components/Jobs/JobList';
import { JobWithEmployer } from '../services/jobService';
import cvTemplateService from '../services/cvTemplateService';
import savedJobService from '../services/savedJobService';
import { getLocalSavedJobs } from '../services/localSavedJobs';
import { useAuth } from '../contexts/AuthContext';
import './JobSearchPage.scss';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export interface FilterOptions {
  searchKeyword: string;
  keywords: string[];
  location: string[];
  workField: string[];
  positionType: string[];
  positions: string[];
  companyActivity: string;
  companyName: string;
  companyNames: string[];
  employerId?: number;
  jobType: string[];
  workTime: string[];
  salaryMin: string;
  postedWithin: string;
  language: string[];
  directEmployersOnly: boolean;
  hideApplied: boolean;
  hideMarked: boolean;
  hideWithoutSalary: boolean;
}

// Convert frontend filters to backend query params (used by /api/jobs/count)
const filtersToQueryParams = (filters: FilterOptions): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.searchKeyword) params.set('query', filters.searchKeyword);
  if (filters.keywords.length > 0) params.set('keywords', filters.keywords.join(','));
  if (filters.location.length > 0) params.set('location', filters.location.join(','));
  if (filters.workField.length > 0) params.set('workField', filters.workField.join(','));
  if (filters.positionType.length > 0) params.set('positionType', filters.positionType.join(','));
  if (filters.positions.length > 0) params.set('positions', filters.positions.join(','));
  if (filters.jobType.length > 0) params.set('jobType', filters.jobType.join(','));
  if (filters.workTime.length > 0) params.set('workTime', filters.workTime.join(','));
  if (filters.salaryMin) params.set('salaryMin', filters.salaryMin);
  if (filters.postedWithin) params.set('postedWithin', filters.postedWithin);
  if (filters.language.length > 0) params.set('language', filters.language.join(','));
  if (filters.companyActivity) params.set('companyActivity', filters.companyActivity);
  if (filters.companyName) params.set('companyName', filters.companyName);
  if (filters.companyNames.length > 0) params.set('companyNames', filters.companyNames.join(','));
  if (filters.employerId) params.set('employerId', filters.employerId.toString());
  if (filters.hideApplied) params.set('hideApplied', 'true');
  if (filters.hideMarked) params.set('hideMarked', 'true');
  return params;
};

const SORT_MAP: Record<string, string> = {
  date: 'created_at:desc',
  deadline: 'application_deadline:asc',
  salary: 'salary_min:desc',
};

// Convert frontend filters to Meilisearch smart search params
const filtersToSmartParams = (filters: FilterOptions): URLSearchParams => {
  const params = new URLSearchParams();
  const qParts: string[] = [];
  if (filters.searchKeyword) qParts.push(filters.searchKeyword);
  if (filters.keywords.length > 0) qParts.push(...filters.keywords);
  if (qParts.length > 0) params.set('q', qParts.join(' '));
  if (filters.location.length > 0) params.set('location', filters.location.join(','));
  if (filters.workField.length > 0) params.set('work_field', filters.workField.join(','));
  if (filters.jobType.length > 0) params.set('job_type', filters.jobType.join(','));
  if (filters.workTime.length > 0) params.set('work_time', filters.workTime.join(','));
  if (filters.positionType.length > 0) params.set('position_type', filters.positionType.join(','));
  if (filters.positions.length > 0) params.set('positions', filters.positions.join(','));
  if (filters.salaryMin) params.set('salary_min', filters.salaryMin);
  if (filters.postedWithin) params.set('postedWithin', filters.postedWithin);
  if (filters.language.length > 0) params.set('language', filters.language.join(','));
  if (filters.companyActivity) params.set('company_activity', filters.companyActivity);
  if (filters.companyName) params.set('company_name', filters.companyName);
  if (filters.companyNames.length > 0) params.set('companyNames', filters.companyNames.join(','));
  if (filters.employerId) params.set('employerId', filters.employerId.toString());
  if (filters.hideApplied) params.set('hideApplied', 'true');
  if (filters.hideMarked) params.set('hideMarked', 'true');
  return params;
};

const JobSearchPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState<JobWithEmployer[]>([]);
  const [promotedJobIds, setPromotedJobIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [gridLayout, setGridLayout] = useState<'grid-3' | 'grid-2' | 'list'>(() => {
    const saved = localStorage.getItem('jobSearchLayout');
    return (saved === 'grid-3' || saved === 'grid-2' || saved === 'list') ? saved : 'grid-3';
  });
  const [showFilters, setShowFilters] = useState(() => window.innerWidth >= 768);
  const [filtersClosing, setFiltersClosing] = useState(false);

  const handleCollapseFilters = useCallback(() => {
    setFiltersClosing(true);
    setTimeout(() => {
      setShowFilters(false);
      setFiltersClosing(false);
    }, 250);
  }, []);
  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = parseInt(searchParams.get('page') || '1');
    return pageParam > 0 ? pageParam : 1;
  });
  const [totalResults, setTotalResults] = useState(0);
  const [liveCount, setLiveCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [categoryImages, setCategoryImages] = useState<{ [value: string]: string | null }>({});
  const [sortBy, setSortBy] = useState<'date' | 'deadline' | 'salary'>('date');
  const [savedJobIds, setSavedJobIds] = useState<Set<number>>(new Set());
  const [facets, setFacets] = useState<Record<string, Record<string, number>>>({});
  const jobsPerPage = 20;
  const countDebounceRef = useRef<NodeJS.Timeout | null>(null);
  


  useEffect(() => {
    if (isAuthenticated) {
      savedJobService.getMySavedJobIds().then(ids => {
        setSavedJobIds(new Set(ids));
      }).catch(() => {});
    } else {
      const localIds = getLocalSavedJobs().map(j => j.id);
      setSavedJobIds(new Set(localIds));
    }
  }, [isAuthenticated]);

  // Get initial filters from URL parameters (URL is the source of truth)
  const getInitialFilters = (): FilterOptions => {
    const workFieldParam = searchParams.get('workField');
    const positionTypeParam = searchParams.get('positionType');
    const positionsParam = searchParams.get('positions');
    const locationParam = searchParams.get('location');
    const jobTypeParam = searchParams.get('jobType');
    const workTimeParam = searchParams.get('workTime');
    const keywordsParam = searchParams.get('keywords');
    const languageParam = searchParams.get('language');
    const postedWithinParam = searchParams.get('postedWithin');
    const companyNamesParam = searchParams.get('companyNames');
    
    return {
      searchKeyword: searchParams.get('query') || searchParams.get('q') || '',
      keywords: keywordsParam ? keywordsParam.split(',') : [],
      location: locationParam ? locationParam.split(',') : [],
      workField: workFieldParam ? workFieldParam.split(',') : [],
      positionType: positionTypeParam ? positionTypeParam.split(',') : [],
      positions: positionsParam ? positionsParam.split(',') : [],
      companyActivity: searchParams.get('companyActivity') || '',
      companyName: searchParams.get('companyName') || searchParams.get('company') || '',
      companyNames: companyNamesParam ? companyNamesParam.split(',') : [],
      employerId: searchParams.get('employerId') ? parseInt(searchParams.get('employerId')!) : undefined,
      jobType: jobTypeParam ? jobTypeParam.split(',') : [],
      workTime: workTimeParam ? workTimeParam.split(',') : [],
      salaryMin: searchParams.get('salaryMin') || '',
      postedWithin: postedWithinParam || '',
      language: languageParam ? languageParam.split(',') : [],
      directEmployersOnly: searchParams.get('directEmployersOnly') === 'true',
      hideApplied: searchParams.get('hideApplied') === 'true',
      hideMarked: searchParams.get('hideMarked') === 'true',
      hideWithoutSalary: false
    };
  };

  const [filters, setFilters] = useState<FilterOptions>(getInitialFilters());

  const fetchPromotedJobIds = async (): Promise<Set<number>> => {
    try {
      const response = await fetch(`${API_URL}/services/promoted-jobs`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const ids = new Set<number>(result.data as number[]);
          setPromotedJobIds(ids);
          return ids;
        }
      }
    } catch (error) {
      console.error('Error fetching promoted jobs:', error);
    }
    return new Set<number>();
  };

  const sortJobsWithPromotedFirst = (jobList: JobWithEmployer[], promotedIds: Set<number>, sort: 'date' | 'deadline' | 'salary' = 'date') => {
    return [...jobList].sort((a, b) => {
      const aIsPromoted = promotedIds.has(a.id!);
      const bIsPromoted = promotedIds.has(b.id!);
      
      if (aIsPromoted && !bIsPromoted) return -1;
      if (!aIsPromoted && bIsPromoted) return 1;

      if (sort === 'salary') {
        const aSalary = a.salary_max || a.salary_min || 0;
        const bSalary = b.salary_max || b.salary_min || 0;
        return bSalary - aSalary;
      }
      if (sort === 'deadline') {
        const aDeadline = (a as any).application_deadline ? new Date((a as any).application_deadline).getTime() : Infinity;
        const bDeadline = (b as any).application_deadline ? new Date((b as any).application_deadline).getTime() : Infinity;
        return aDeadline - bDeadline;
      }
      return 0;
    });
  };

  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('auth_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Fetch live count from backend (debounced)
  const fetchLiveCount = useCallback(async (filterOptions: FilterOptions) => {
    if (countDebounceRef.current) {
      clearTimeout(countDebounceRef.current);
    }

    countDebounceRef.current = setTimeout(async () => {
      try {
        setCountLoading(true);
        
        const queryParams = filtersToQueryParams(filterOptions);
        const url = `${API_URL}/jobs/count?${queryParams.toString()}`;
        
        const response = await fetch(url, { headers: getAuthHeaders() });
        
        if (response.ok) {
          const result = await response.json();
          setLiveCount(result.count);
        }
      } catch (error) {
        console.error('Error fetching count:', error);
      } finally {
        setCountLoading(false);
      }
    }, 300);
  }, []);

  // Fetch jobs from Meilisearch smart endpoint (with MySQL fallback)
  const fetchJobs = useCallback(async (filterOptions: FilterOptions, page: number = 1, sort?: 'date' | 'deadline' | 'salary') => {
    try {
      setLoading(true);

      const activeSort = sort ?? sortBy;

      const queryParams = filtersToSmartParams(filterOptions);
      queryParams.set('page', page.toString());
      queryParams.set('limit', jobsPerPage.toString());
      queryParams.set('sortBy', SORT_MAP[activeSort] || 'created_at:desc');

      const [jobsResponse, promotedIds] = await Promise.all([
        fetch(`${API_URL}/jobs/search/smart?${queryParams.toString()}`, {
          headers: getAuthHeaders()
        }),
        fetchPromotedJobIds()
      ]);

      if (jobsResponse.ok) {
        const result = await jobsResponse.json();
        let allJobs = result.data?.jobs || [];
        const total = result.pagination?.total || allJobs.length;

        // Guest users: apply hideMarked client-side (backend can't do it without a token)
        if (!isAuthenticated && filterOptions.hideMarked && savedJobIds.size > 0) {
          allJobs = allJobs.filter((j: any) => !savedJobIds.has(j.id));
        }

        const sortedJobs = sortJobsWithPromotedFirst(allJobs, promotedIds, activeSort);

        setJobs(sortedJobs);
        setTotalResults(total);
        setLiveCount(total);
        setFacets(result.data?.facets || {});
      } else {
        console.error('Failed to fetch jobs');
        setJobs([]);
        setTotalResults(0);
        setFacets({});
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
      setTotalResults(0);
      setFacets({});
    } finally {
      setLoading(false);
    }
  }, [sortBy, savedJobIds, isAuthenticated]);

  // Apply filters - fetch from backend with all filters
  const applyFilters = useCallback(async (newFilters: FilterOptions) => {
    console.log('🔍 Applying filters:', newFilters);
    
    // Update URL with all filters (so back button preserves state)
    const newParams = filtersToQueryParams(newFilters);
    setSearchParams(newParams, { replace: true });
    
    // Update filters state
    setFilters(newFilters);
    setCurrentPage(1);
    
    // Fetch filtered results from backend
    await fetchJobs(newFilters, 1);
  }, [fetchJobs, setSearchParams]);

  // Handle filter preview (just update count without fetching full results)
  const previewFilters = useCallback((newFilters: FilterOptions) => {
    fetchLiveCount(newFilters);
  }, [fetchLiveCount]);

  const getCurrentPageJobs = () => jobs;

  const handlePageChange = async (page: number) => {
    setCurrentPage(page);
    const newParams = new URLSearchParams(searchParams);
    if (page > 1) {
      newParams.set('page', page.toString());
    } else {
      newParams.delete('page');
    }
    setSearchParams(newParams, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    await fetchJobs(filters, page);
  };

  const totalPages = Math.ceil(totalResults / jobsPerPage);

  const searchParamsString = searchParams.toString();

  useEffect(() => {
    const urlFilters = getInitialFilters();
    const pageParam = parseInt(searchParams.get('page') || '1');
    const initialPage = pageParam > 0 ? pageParam : 1;
    setFilters(urlFilters);
    setCurrentPage(initialPage);
    fetchJobs(urlFilters, initialPage);

    Promise.all([
      cvTemplateService.getCategoryImages('company_activity'),
      cvTemplateService.getCategoryImages('work_field')
    ]).then(([activityImages, workFieldImages]) => {
      setCategoryImages({ ...workFieldImages, ...activityImages });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, searchParamsString]);

  return (
    <div className="job-search-page">
      <div className="job-search-content">
        {showFilters && (
          <div className={`filter-sidebar${filtersClosing ? ' closing' : ''}`}>
            <div className="filter-sidebar-inner">
              <JobSearchFilters 
                isVisible={true}
                onFiltersChange={applyFilters}
                onFiltersPreview={previewFilters}
                onCollapse={handleCollapseFilters}
                currentFilters={filters}
                liveCount={liveCount}
                countLoading={countLoading}
                facets={facets}
              />
            </div>
          </div>
        )}

        <div className="main-content">
          <div className="content-header">
            <div className="content-header-top">
              <div className="header-left">
                {!showFilters && (
                  <button
                    className="sidebar-reopen-btn"
                    onClick={() => setShowFilters(true)}
                    aria-label="Atidaryti filtrus"
                  >
                    <ChevronRight size={16} />
                  </button>
                )}
                <div>
                  <div className="breadcrumb">
                    <span>Titulinis</span>
                    <span>›</span>
                    <span>Nauja paieška</span>
                  </div>
                  <h2>
                    {filters.companyName || filters.employerId
                      ? `${filters.companyName || 'Įmonės'} skelbimai (${totalResults})`
                      : `Darbo skelbimai (${totalResults})`
                    }
                  </h2>
                </div>
              </div>
            </div>

            <div className="content-header-controls">
              <div className="sort-controls">
                <label className="sort-label">Rūšiuoti pagal</label>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    const newSort = e.target.value as 'date' | 'deadline' | 'salary';
                    setSortBy(newSort);
                    fetchJobs(filters, currentPage, newSort);
                  }}
                >
                  <option value="date">Paskelbimo datą</option>
                  <option value="deadline">Galiojimo datą</option>
                  <option value="salary">Atlyginimą</option>
                </select>
              </div>

              <div className="quick-filters">
                <label className="filter-checkbox">
                  <input 
                    type="checkbox" 
                    checked={filters.hideApplied}
                    onChange={(e) => {
                      const newFilters = { ...filters, hideApplied: e.target.checked };
                      applyFilters(newFilters);
                    }}
                  />
                  <span className="checkmark"></span>
                  <span className="label-text">Nerodyti kandidatuotų</span>
                </label>
                <label className="filter-checkbox">
                  <input 
                    type="checkbox" 
                    checked={filters.hideMarked}
                    onChange={(e) => {
                      const newFilters = { ...filters, hideMarked: e.target.checked };
                      applyFilters(newFilters);
                    }}
                  />
                  <span className="checkmark"></span>
                  <span className="label-text">Nerodyti pažymėtų</span>
                </label>
              </div>

              <div className="pagination-info">
                <span className="page-info">{currentPage} iš {totalPages}</span>
                <div className="page-arrows">
                  <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="page-arrow">‹</button>
                  <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="page-arrow">›</button>
                </div>
                <div className="layout-selector">
                  <button className={`layout-grid-btn ${gridLayout !== 'list' ? 'active' : ''}`} onClick={() => { setGridLayout('grid-3'); localStorage.setItem('jobSearchLayout', 'grid-3'); }}>
                    <div className="grid-icon"></div>
                    <div className="grid-icon"></div>
                    <div className="grid-icon"></div>
                    <div className="grid-icon"></div>
                  </button>
                  <button className={`layout-list-btn ${gridLayout === 'list' ? 'active' : ''}`} onClick={() => { setGridLayout('list'); localStorage.setItem('jobSearchLayout', 'list'); }}>
                    <div className="list-line"></div>
                    <div className="list-line"></div>
                    <div className="list-line"></div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <JobList
            jobs={getCurrentPageJobs()}
            layout={gridLayout}
            loading={loading}
            promotedJobIds={promotedJobIds}
            categoryImages={categoryImages}
            savedJobIds={savedJobIds}
            onSaveToggle={(jobId, saved) => {
              setSavedJobIds(prev => {
                const next = new Set(prev);
                saved ? next.add(jobId) : next.delete(jobId);
                return next;
              });
            }}
          />

          {totalPages > 1 && (
            <div className="pagination">
              <span className="page-info">{currentPage} iš {totalPages}</span>
              <div className="page-controls">
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="page-btn">‹</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`page-number ${currentPage === pageNum ? 'active' : ''}`}>
                      {pageNum}
                    </button>
                  );
                })}
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="page-btn">›</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobSearchPage;

